




















































import type { PropType } from "@nuxtjs/composition-api";
import {
  computed,
  defineComponent,
  toRef,
  useContext,
  useRouter,
} from "@nuxtjs/composition-api";
import JetUiButton from "~/components/JetUi/atoms/JetUiButton/JetUiButton.vue";
import { CustomerAddress } from "~/modules/GraphQL/types";
import type { TransformedCustomerAddress } from "~/modules/customer/composables/types";
import { useAddresses } from "~/modules/customer/composables/useAddresses";
import userAddressesGetters from "~/modules/customer/getters/userAddressesGetters";
import { useCheckout, useCheckoutStore } from "~/stores/checkout";

export default defineComponent({
  name: "UserAddressDetails",
  components: {
    JetUiButton,
  },
  props: {
    address: {
      type: Object as PropType<TransformedCustomerAddress>,
      required: true,
    },
    originMethods: {
      type: String,
      default: "",
    },
  },
  emits: ["reloadAddress"],
  setup(props, { emit }) {
    const address = toRef(props, "address");
    const context = useContext();
    const router = useRouter();
    const { remove } = useAddresses();
    const { onLoading, getCurrentAddressId } = useCheckoutStore();
    const { loadAddresses, verifyCurrentAddress } = useCheckout(
      props.originMethods ?? "shipping",
      false
    );

    const isActive = computed(
      () =>
        getCurrentAddressId(props.originMethods ?? "shipping") ===
        userAddressesGetters.getId(address.value)
    );
    const userAddress = computed(() => ({
      id: userAddressesGetters.getId(address.value),
      firstName: userAddressesGetters.getFirstName(address.value),
      lastName: userAddressesGetters.getLastName(address.value),
      street: userAddressesGetters.getStreetName(address.value),
      streetNumber: userAddressesGetters.getApartmentNumber(address.value),
      postalCode: userAddressesGetters.getPostCode(address.value),
      city: userAddressesGetters.getCity(address.value),
      province: userAddressesGetters.getProvince(address.value) || "",
      country: userAddressesGetters.getCountry(address.value),
      phone: userAddressesGetters.getPhone(address.value),
      isDefault: userAddressesGetters.isDefault(address.value),
      isDefaultShipping: userAddressesGetters.isDefaultShipping(address.value),
      isDefaultBilling: userAddressesGetters.isDefaultBilling(address.value),
    }));

    const goToEditAddressPage = (addressId: number) =>
      router.push(
        context.localeRoute({
          name: "customer-addresses-details-edit",
          params: { addressId: String(addressId) },
        })
      );

    const removeAddress = async (address: CustomerAddress) => {
      onLoading(true);
      await remove({ address });
      await loadAddresses();
      await verifyCurrentAddress();
      onLoading(false);
      emit("reloadAddress");
    };

    const isDefaultShippingText = computed(() =>
      userAddress.value.isDefaultShipping ? "Shipping Address" : ""
    );

    const isDefaultBillingText = computed(() =>
      userAddress.value.isDefaultBilling ? "Billing Address" : ""
    );

    return {
      goToEditAddressPage,
      removeAddress,
      userAddress,
      isDefaultShippingText,
      isDefaultBillingText,
      isActive,
    };
  },
});
